import React, { useMemo } from 'react'

// SparkleParticles: wraps children with animated sparkles/particles (now white circles)
// Usage: <SparkleParticles><button>...</button></SparkleParticles>
const SparkleParticles: React.FC<{
  count?: number // Number of sparkles
  size?: number // Max sparkle size in px
  children: React.ReactNode
}> = ({ count = 5, size = 12, children }) => {
  // Generate sparkle configs only once per mount
  const sparkles = useMemo(() => {
    return Array.from({ length: count }).map((_, i) => {
      // Random position around the container (as %)
      const angle = Math.random() * 2 * Math.PI
      const radius = 32 + Math.random() * 14 // px from center
      const x = 50 + Math.cos(angle) * radius // %
      const y = 50 + Math.sin(angle) * radius // %
      // Random size and animation
      const sparkleSize = size * (0.5 + Math.random() * 0.7)
      const duration = 1.2 + Math.random() * 1.2 // seconds
      const delay = Math.random() * 1.2 // seconds
      const opacity = 0.45 + Math.random() * 0.35 // More subtle
      return { x, y, sparkleSize, duration, delay, opacity, i }
    })
  }, [count, size])

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      {/* Render sparkles absolutely around the child */}
      {sparkles.map(s => (
        <span
          key={s.i}
          className="sparkle-particle"
          style={{
            position: 'absolute',
            left: `${s.x}%`,
            top: `${s.y}%`,
            width: s.sparkleSize,
            height: s.sparkleSize,
            pointerEvents: 'none',
            zIndex: 2,
            animation: `sparkle-float ${s.duration}s linear ${s.delay}s infinite`,
            opacity: s.opacity,
            filter: 'blur(0.5px) drop-shadow(0 0 6px #fff8)',
          }}
        >
          {/* White circle with soft glow */}
          <svg width={s.sparkleSize} height={s.sparkleSize} viewBox={`0 0 ${s.sparkleSize} ${s.sparkleSize}`} fill="none">
            <circle cx={s.sparkleSize/2} cy={s.sparkleSize/2} r={s.sparkleSize/2} fill="#fff" />
          </svg>
        </span>
      ))}
      {/* Render the wrapped child */}
      {children}
    </span>
  )
}

export default SparkleParticles 